/**
 * Role-based sidebar filtering: each navigation entry may be tagged with the
 * application it belongs to (`applicationCode`, matching the OPUS catalog codes
 * delivered on the profile payload — "ophub", "opink", …). Tagged entries are
 * shown only when the user can access that application; untagged entries are
 * cross-cutting (Home, Settings, …) and always shown.
 *
 * This module also owns the navigation-item types. They historically lived in
 * menuVisibility.ts (the temporary per-environment allow-list), which re-exports
 * them for compatibility until that layer is deleted.
 */
/**
 * Keeps only the entries whose application the user can access.
 *
 * - `accessibleCodes` empty → the input is returned as-is (NO filtering). This
 *   fail-open is deliberate, and it knowingly conflates three states that are
 *   byte-identical on the wire today: profile not yet loaded, a genuinely empty
 *   grant, and the BFF's own fail-open (it substitutes an empty list while the
 *   OPUS application-catalog endpoint is not live — see the fail-open TODO on
 *   UserProfileService.GetAccessibleApplicationsAsync in the BFF). Because every
 *   environment currently receives an empty set for every user, treating empty
 *   as "no access" would blank the menu for everyone; and the menu is only
 *   affordance-hiding — the per-service backend rights checks (403) remain the
 *   enforcement point, and most application-tagged routes carry no frontend
 *   capability guard at all (only /scheduling, /reception/* and
 *   /workspace/users declare meta.requiresCapability) — so failing open
 *   grants nothing. The strict empty-set behaviour (cross-cutting
 *   entries only, no-application landing) belongs to the landing/switcher story
 *   and becomes distinguishable through useApplicationAccess (`isReady` vs
 *   `hasApplicationData`) once the BFF reverts to fail-closed; this function
 *   deliberately stays a pure membership filter until then.
 * - Untagged entries are cross-cutting and always kept.
 * - Tagged entries are kept iff their `applicationCode` is in the accessible
 *   set, compared case-insensitively on both sides.
 * - Section keys carry no tag of their own: children are filtered individually
 *   and a section survives iff at least one child does (same "a section follows
 *   its children" rule as the allow-list filter).
 * - Unlike `filterNavigation`, there is NO empty-result fallback: the Home entry
 *   is untagged, so the result always contains at least one entry and an empty
 *   sidebar is unreachable by construction.
 *
 * Pure: never mutates `items`, preserves input order. Callers apply their
 * capability/flag gates FIRST and the per-environment allow-list AFTER, so the
 * allow-list can only narrow this already-access-filtered set.
 */
export function filterByApplication(items, accessibleCodes) {
    if (accessibleCodes.length === 0) {
        return items;
    }
    const accessible = new Set(accessibleCodes.map((code) => code.toLowerCase()));
    const isAccessible = (code) => !code || accessible.has(code.toLowerCase());
    return items.reduce((acc, item) => {
        if (item.children?.length) {
            const children = item.children.filter((child) => isAccessible(child.applicationCode));
            if (children.length > 0) {
                acc.push(children.length === item.children.length ? item : { ...item, children });
            }
            return acc;
        }
        if (isAccessible(item.applicationCode)) {
            acc.push(item);
        }
        return acc;
    }, []);
}
